import { useEffect, useState } from 'react'
import { Table, Button, Input, Space, Tag, Typography } from 'antd'
import { ArrowRight, Plus, Search } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { formatDate } from '../../../utils/date'
import { useCompanyStore } from '../../../store/company/companyStore'
import { RoleEnum, TeamMember } from '../../../types/ffe'
import { useApi } from '../../../services/ApiContext'

const { Text } = Typography

export const UserList = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const navigate = useNavigate()
  const teamMembers = useCompanyStore((state) => state.teamMembers)
  const api = useApi()
  const setTeamMembers = useCompanyStore((state) => state.setTeamMembers)
  const plan = useCompanyStore((state) => state.company?.plan)

  const filteredUsers = teamMembers.filter(
    (user) =>
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  useEffect(() => {
    // Fetch team members
    api.loadTeamMembers().then(setTeamMembers)
  }, [])

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_text: string, record: TeamMember) => (
        <div>
          <Text strong className="block">
            {record.firstName} {record.lastName}
          </Text>
          <Text type="secondary" className="text-sm">
            {record.email}
          </Text>
        </div>
      )
    },
    {
      title: 'Role',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles: TeamMember['roles']) => (
        <Tag>{roles.map((role) => RoleEnum[role]).join(', ')}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'isDisabled',
      key: 'isDisabled',
      render: (isDisabled?: boolean) => (
        <Tag color={!isDisabled ? 'success' : 'error'}>
          {!isDisabled ? 'Active' : 'Disabled'}
        </Tag>
      )
    },
    {
      title: 'Last Login',
      dataIndex: 'lastActive',
      key: 'lastActive',
      render: (date: string) => (date ? formatDate(date) : '-')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: TeamMember) => (
        <Button
          type="link"
          onClick={() => navigate(`/app/settings/users/${record._id}`)}
        >
          Manage
        </Button>
      )
    }
  ]

  if (!plan || plan === 'free') {
    return (
      <div className="flex gap-4 items-center">
        <Text className="text-md font-semibold">
          Upgrade to add more team members
        </Text>
        <Link to="/app/upgrade">
          <Button type="primary">
            See plans <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Input
          prefix={<Search className="h-4 w-4 text-gray-400" />}
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-xs"
        />
        <Space>
          <Button
            type="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/app/settings/users/new')}
          >
            Invite Team Member
          </Button>
        </Space>
      </div>

      <Table columns={columns} dataSource={filteredUsers} rowKey="_id" />
    </div>
  )
}
